import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useMarketing } from "@/components/marketing/MarketingLayout";
import {
  ArrowRight, ArrowsClockwise, FileText, ShieldCheck, ChartLineUp, Robot, Check,
} from "@phosphor-icons/react";
import {
  fadeUp, stagger, Reveal, Overline, EditorialHero, SectionHeader, GlassCard,
} from "@/components/marketing/editorial";

const CAPS = [
  { icon: ArrowsClockwise, t: "Reconciliation AI", d: "Automatically match transactions across systems, flag exceptions, and resolve discrepancies without manual intervention — across bank statements, ledgers and intercompany accounts.",
    f: ["Multi-system transaction matching", "Exception detection & prioritization", "Auto-resolution rule engine", "Continuous reconciliation monitoring"] },
  { icon: FileText, t: "Document AI", d: "Extract, classify and process financial documents with AI precision — invoices, remittances, contracts and receipts — then push structured data directly to your ERP.",
    f: ["Automated invoice extraction", "Smart remittance capture & matching", "Contract key-term extraction", "Multi-format document recognition"] },
  { icon: ShieldCheck, t: "Compliance AI", d: "Monitor regulatory compliance automatically with real-time policy enforcement and exception flagging — SOX controls, expense policies, vendor limits and approval thresholds.",
    f: ["Automated regulatory monitoring", "Policy enforcement & validation", "Risk assessment & exception flagging", "Compliance reporting automation"] },
  { icon: ChartLineUp, t: "Analytics AI", d: "Transform raw financial data into actionable insight — variance narratives, trends and anomalies surfaced before they become problems, without a single manual query.",
    f: ["Variance analysis with AI narrative", "Trend & anomaly detection", "Predictive forecasting from live ERP data", "Custom KPI monitoring & alerting"] },
  { icon: Robot, t: "Automation AI", d: "Orchestrate end-to-end finance workflows — from routine AP processing to multi-entity close coordination — with human-in-the-loop controls.",
    f: ["End-to-end workflow orchestration", "Multi-system integration & handoffs", "Human-in-the-loop approval gates", "Exception routing & escalation"] },
];

const SUITE = [
  ["FP&A Agent", "Rolling forecasts, variance analysis and planning automation for CFO teams.", "10× faster reporting"],
  ["Close Automation", "Accelerate month-end close with task orchestration and automated journal entries.", "75% close time cut"],
  ["Reconciliation Agent", "AI matching and exception handling at scale — bank, intercompany and ledger.", "99%+ accuracy"],
  ["Document Processing", "Extract and process invoices, contracts and receipts with PO matching.", "1.2s per document"],
  ["AP Automation", "Payables from invoice receipt to payment, with 3-way match and approval routing.", "8× faster processing"],
  ["AR Automation", "Accelerate collections, automate cash application and improve DSO.", "35% DSO reduction"],
];

const STEPS = [
  ["01", "Connect your systems", "Authenticate through native connectors — Oracle Fusion, Oracle EBS, Dynamics 365, IFS. No middleware, ~30 min setup."],
  ["02", "AI processes & executes", "Agents analyze data, enforce your rules, and execute workflows directly inside the ERP with approval gates."],
  ["03", "Monitor & improve", "Track agent performance, review exceptions, and expand automation. Accuracy improves every cycle."],
];

export default function ProductsPage() {
  const { openDemo } = useMarketing();
  return (
    <>
      <EditorialHero
        eyebrow="End-to-end finance automation"
        title="AI that runs"
        italic="finance workflows."
        sub="Intelligent agents that understand, automate and optimize your entire financial operations stack — connected directly to Oracle Fusion, Oracle EBS, Microsoft Dynamics 365 and IFS.">
        <div className="flex flex-wrap gap-3">
          <button onClick={openDemo} data-testid="products-book-demo"
            className="group rounded-full px-7 py-3.5 font-semibold text-white transition-all hover:-translate-y-0.5 inline-flex items-center gap-2"
            style={{ background: "#0B1E3F", boxShadow: "0 12px 32px rgba(11,30,63,0.20)" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#C8A24B")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#0B1E3F")}>
            Book a Demo <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
          </button>
          <Link to="/use-cases" data-testid="products-use-cases"
            className="bg-white rounded-full px-7 py-3.5 font-semibold inline-flex items-center gap-2 transition-all"
            style={{ color: "#0B1E3F", border: "1px solid rgba(11,30,63,0.20)" }}>
            View use cases <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </EditorialHero>

      {/* Five core capabilities — white, navy, gold accents */}
      <section className="py-24 sm:py-32 border-t bg-white"
               style={{ borderColor: "rgba(11,30,63,0.10)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeader eyebrow="Five core capabilities · full finance coverage"
            title="Built for the modern" italic="finance back office." />
          <motion.div className="space-y-px rounded-2xl overflow-hidden"
            style={{ background: "rgba(11,30,63,0.06)", border: "1px solid rgba(11,30,63,0.12)" }}
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
            variants={stagger}>
            {CAPS.map(({ icon: Icon, t, d, f }) => (
              <motion.div key={t} variants={fadeUp}
                className="p-8 md:p-12 grid md:grid-cols-[1fr_1fr] gap-10 transition-colors bg-white"
                data-testid={`product-${t.replace(/\s+/g, '-').toLowerCase()}`}>
                <div>
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl"
                       style={{ background: "rgba(200,162,75,0.12)", border: "1px solid rgba(200,162,75,0.40)", color: "#C8A24B" }}>
                    <Icon size={22} weight="duotone" />
                  </div>
                  <h3 className="font-serif text-3xl sm:text-4xl font-medium tracking-tight mt-5" style={{ color: "#0B1E3F" }}>{t}</h3>
                  <p className="font-light mt-4 leading-relaxed" style={{ color: "rgba(11,30,63,0.66)" }}>{d}</p>
                </div>
                <ul className="grid sm:grid-cols-2 gap-3 self-center">
                  {f.map((x) => (
                    <li key={x} className="flex gap-2.5 text-sm" style={{ color: "rgba(11,30,63,0.78)" }}>
                      <Check size={16} weight="bold" className="shrink-0 mt-0.5" style={{ color: "#1F6B4A" }} /> {x}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Product suite — navy band */}
      <section className="py-24 sm:py-32 border-t"
               style={{ background: "#0B1E3F", borderColor: "rgba(11,30,63,0.10)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-3xl mb-16">
            <Overline>Product suite</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mt-4 leading-[1.05] text-white">
              Purpose-built for every <span className="italic" style={{ color: "#C8A24B" }}>finance function.</span>
            </h2>
          </Reveal>
          <motion.div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5"
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
            {SUITE.map(([t, d, stat]) => (
              <motion.div key={t} variants={fadeUp}
                data-testid={`suite-${t.replace(/\s+/g, '-').toLowerCase()}`}
                className="rounded-2xl p-8 transition-all hover:-translate-y-1"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(200,162,75,0.30)" }}>
                <h3 className="font-serif text-2xl font-medium text-white">{t}</h3>
                <p className="text-sm font-light mt-3 leading-relaxed text-white/70">{d}</p>
                <div className="mt-6 inline-flex items-center gap-2 font-mono font-bold text-sm tracking-wider" style={{ color: "#1F6B4A" }}>
                  <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: "#1F6B4A" }} />
                  {stat}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How it works — white */}
      <section className="py-24 sm:py-32 border-t bg-white"
               style={{ borderColor: "rgba(11,30,63,0.10)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeader eyebrow="How Vaku works"
            title="From data to decision in" italic="three steps." />
          <div className="grid md:grid-cols-3 gap-5">
            {STEPS.map(([n, t, d], i) => (
              <Reveal key={n} delay={i * 0.1}>
                <GlassCard className="h-full">
                  <div className="font-serif text-5xl font-light" style={{ color: "rgba(200,162,75,0.85)" }}>{n}</div>
                  <h3 className="font-serif text-2xl font-medium mt-4" style={{ color: "#0B1E3F" }}>{t}</h3>
                  <p className="text-sm font-light mt-3 leading-relaxed" style={{ color: "rgba(11,30,63,0.66)" }}>{d}</p>
                </GlassCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* White CTA */}
      <section className="bg-white py-24 sm:py-28 border-t" style={{ borderColor: "rgba(11,30,63,0.10)" }}>
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <Reveal>
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] font-semibold" style={{ color: "#C8A24B" }}>Ready when you are</div>
            <h3 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter leading-[1.02]" style={{ color: "#0B1E3F" }}>
              See it run on <span className="italic" style={{ color: "#C8A24B" }}>your data</span>.
            </h3>
            <p className="mt-6 text-lg font-light max-w-xl mx-auto" style={{ color: "rgba(11,30,63,0.70)" }}>
              We&apos;ll plug into a sandbox of your ERP and execute one workflow end-to-end during the first 30-minute demo.
            </p>
            <div className="mt-8">
              <button onClick={openDemo} data-testid="products-cta-demo"
                className="group rounded-full px-8 py-4 font-semibold transition-all hover:-translate-y-0.5 inline-flex items-center gap-2 text-white"
                style={{ background: "#0B1E3F", boxShadow: "0 12px 32px rgba(11,30,63,0.25)" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#C8A24B")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#0B1E3F")}>
                Schedule a Demo <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
